import React, { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

// Lazy load ThreeSphere
const ThreeSphere = React.lazy(() => import('shared/components/ThreeSphere'));

function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [shouldLoadSphere, setShouldLoadSphere] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
    const timeout = setTimeout(() => setShouldLoadSphere(true), 200); // delay render
    return () => clearTimeout(timeout);
  }, []);

  async function handleRegister(e) {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const res = await axios.post('/api/myteconstruction/auth/register', {
        name,
        email,
        password,
        confirmPassword,
        acceptTerms,
        user_type: 'Foreman',
      });
      setMessage(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Sphere Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {shouldLoadSphere && (
          <Suspense fallback={<div className="w-full h-full bg-black" />}>
            <ThreeSphere noAutoTransition zoomMultiplier={isMobile ? 2.5 : 3.5} />
          </Suspense>
        )}
      </div>

      {/* Registration Card */}
      <div className="relative z-10 max-w-md w-full bg-gray-900 bg-opacity-100 sm:bg-opacity-90 p-4 sm:p-8 rounded-lg shadow-lg">
        <div className="mb-6 text-center">
          <img src="/LogoFile.png" alt="Myte Construction Logo" className="mx-auto w-24 mb-4" />
          <h2 className="text-3xl font-bold text-white">Create Your Account</h2>
          <p className="text-blue-300 mt-2 text-sm">Experience AI-driven construction management.</p>
        </div>

        {message && <div className="text-green-400 text-center mb-3">{message}</div>}
        {error && <div className="text-red-500 text-center mb-3">{error}</div>}

        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-white font-medium mb-1">
              Full Name
            </label>
            <input
              id="name"
              type="text"
              className="w-full px-4 py-2 rounded-md bg-gray-800 text-white"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-white font-medium mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="w-full px-4 py-2 rounded-md bg-gray-800 text-white"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-white font-medium mb-1">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="w-full px-4 py-2 rounded-md bg-gray-800 text-white pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-white font-medium mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                className="w-full px-4 py-2 rounded-md bg-gray-800 text-white pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                required
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="terms"
              className="mr-2"
              checked={acceptTerms}
              onChange={() => setAcceptTerms(!acceptTerms)}
              required
            />
            <label htmlFor="terms" className="text-sm text-gray-300">
              I agree to the{' '}
              <a
                href="https://mytegroup.ai/termsandconditions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-blue-400"
              >
                Terms & Conditions
              </a>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold transition duration-200"
          >
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-400">
            Already have an account?{' '}
            <Link to="/myteconstruction/login" className="text-blue-400 hover:underline">
              Login here
            </Link>
          </p>
        </div>
        <div className="mt-2 text-center">
          <Link to="/myteconstruction" className="text-sm text-blue-400 hover:underline">
            Return to Myte Construction
          </Link>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
